import Link from 'next/link'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { motion, cubicBezier } from 'framer-motion'
import { LanguageSwitcher, PeopleList, InstagramLink } from '@/components'
import { BRAND_URL } from '@/libs/const'
import type { PersonType } from '@/@types/microcms-type'

type MenuProps = {
    people?: PersonType[]
    isOpen: boolean
    handleMenu?: () => void
    handleToggleMenu?: () => void
}

export const Menu = ({
    people,
    isOpen,
    handleMenu,
    handleToggleMenu
}: MenuProps) => {
    const { t } = useTranslation('common')

    const ease = cubicBezier(0.25, 0, 0, 1)

    const variants = {
        hidden: { opacity: 0, x: 20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                delay: 0.5,
                duration: 0.8,
                ease: ease
            }
        },
        exit: {
            opacity: 0,
            x: 20,
            transition: {
                duration: 1,
                ease: ease
            }
        }
    }

    return (
        <div
            className={clsx(
                'fixed top-0 right-0 z-10 w-full md:w-auto h-d-screen bg-v-white duration-[500ms] md:text-right px-[10vw] pt-[calc(9vh+40px)] pb-[5vh] md:px-24 grid items-center text-v-navy overflow-y-scroll hidden-scrollbar ease-v-base',
                isOpen
                    ? 'translate-x-[0%] opacity-100'
                    : 'translate-x-[100%] opacity-50'
            )}
        >
            <div className="grid gap-10 md:gap-12 relative z-super">
                <p className="text-v-lg leading-v-heading md:!text-right">
                    <Link
                        href="/"
                        onClick={handleMenu}
                        scroll={false}
                        className="inline-block md:hover:opacity-50 duration-[300ms]"
                    >
                        {t('all_works')}
                    </Link>
                </p>

                <PeopleList
                    people={people}
                    handleMenu={handleMenu}
                    isOpen={isOpen}
                />

                <motion.p
                    initial="hidden"
                    animate={isOpen ? 'visible' : 'hidden'}
                    variants={variants}
                    className="text-v-lg leading-v-heading md:!text-right mb-12 md:mb-14"
                >
                    <Link
                        href={BRAND_URL}
                        onClick={handleMenu}
                        scroll={false}
                        target="_blank"
                        className="inline-block md:hover:opacity-50 duration-[300ms]"
                    >
                        {t('brand_link')}
                    </Link>
                </motion.p>

                <InstagramLink isOpen={isOpen} />
                <LanguageSwitcher handleToggleMenu={handleToggleMenu} />
            </div>
        </div>
    )
}
